import * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import type { PhaseRenderContext } from '@/shared/types/assets'
import type { Disposable } from '@/shared/types/core'
import type {
  SharedOcclusionDepthDiagnostics,
  SharedOcclusionDepthRenderable,
  SharedOcclusionDepthService,
  SharedOcclusionDepthWriter,
} from '@/shared/types/sharedOcclusionDepth'
import type { ViewFrameContext, ViewId } from '@/shared/types/view'

interface AttachedObjectRestore {
  object: THREE.Object3D
  parent: THREE.Object3D | null
  index: number
  matrix: THREE.Matrix4
  matrixWorld: THREE.Matrix4
  position: THREE.Vector3
  quaternion: THREE.Quaternion
  scale: THREE.Vector3
  matrixAutoUpdate: boolean
}

/** 默认共享遮挡深度服务实现。 */
export class SharedOcclusionDepthServiceImpl implements SharedOcclusionDepthService {
  private readonly writers = new Set<SharedOcclusionDepthWriter>()
  private readonly diagnosticsByView = new Map<ViewId, SharedOcclusionDepthDiagnostics>()
  private readonly depthScene = new THREE.Scene()
  private readonly depthMaterial = new THREE.MeshBasicMaterial({
    colorWrite: false,
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
  })
  private latestViewId: ViewId | null = null
  private disposed = false
  /** restore 记录对象池，避免每帧每对象的 clone 分配 */
  private readonly restorePool: AttachedObjectRestore[] = []

  constructor(private readonly onWriterChanged: ((reason: string) => void) | null = null) {}

  /**
   * Registers a depth writer.
   *
   * @param writer - Writer to include in the occlusion depth pass.
   * @returns Disposable registration handle.
   */
  registerWriter(writer: SharedOcclusionDepthWriter): Disposable {
    if (this.disposed) {
      return { dispose: () => {} }
    }

    this.writers.add(writer)
    this.onWriterChanged?.('sharedOcclusionDepth.writerChanged')
    let active = true
    return {
      dispose: () => {
        if (!active) {
          return
        }
        active = false
        this.writers.delete(writer)
        this.onWriterChanged?.('sharedOcclusionDepth.writerChanged')
      },
    }
  }

  /**
   * Gets the latest diagnostics for a view.
   *
   * @param viewId - Optional view id; omitted means the latest rendered view.
   * @returns Latest diagnostics, or null when no frame has been rendered.
   */
  getDiagnostics(viewId?: ViewId): SharedOcclusionDepthDiagnostics | null {
    const targetViewId = viewId ?? this.latestViewId
    if (!targetViewId) {
      return null
    }

    return this.diagnosticsByView.get(targetViewId) ?? null
  }

  /**
   * 执行当前 view frame 的 depth-only pass。
   *
   * 提交统一走 `GraphicsBackend.renderScene` 契约（2026-09 阶段 3 冻结）：viewport/scissor、
   * output target 与 renderer 状态由 backend 依当前 View 决定，本服务不再直接改写 legacy renderer，
   * 因此 WebGL2 与 WebGPU 共用同一条链路。depth-only 语义由 `colorWrite=false` +
   * `depthWrite=true` 的覆盖材质表达，两个后端的材质系统都会遵守这两个开关。
   *
   * @param frame - View frame context.
   * @param context - Render phase context.
   */
  render(frame: ViewFrameContext, context: PhaseRenderContext): void {
    if (this.disposed) {
      return
    }

    const writers = Array.from(this.writers).sort(
      (left, right) => (left.priority ?? 0) - (right.priority ?? 0)
    )
    const renderablesByWriter = new Map<
      SharedOcclusionDepthWriter,
      SharedOcclusionDepthRenderable[]
    >()

    for (const writer of writers) {
      const collected = collectWriterRenderables(writer, frame, context)
      if (collected.length > 0) {
        renderablesByWriter.set(writer, collected)
      }
    }

    const renderableCount = Array.from(renderablesByWriter.values()).reduce(
      (total, entries) => total + entries.length,
      0
    )
    const diagnostics: SharedOcclusionDepthDiagnostics = {
      viewId: frame.viewId,
      frameNumber: frame.frameNumber,
      writerCount: renderablesByWriter.size,
      renderableCount,
      hasDepth: renderableCount > 0,
    }
    this.diagnosticsByView.set(frame.viewId, diagnostics)
    this.latestViewId = frame.viewId

    if (renderableCount === 0) {
      return
    }

    const { graphics } = context
    const renderScene = graphics.renderScene
    if (typeof renderScene !== 'function') {
      throw new UnsupportedGraphicsCapabilityError(
        'shared-occlusion-depth-surface-render',
        graphics.kind,
        '要求 GraphicsBackend.renderScene 受控 surface 提交原语'
      )
    }

    const attached: AttachedObjectRestore[] = []
    const previousOverrideMaterial = this.depthScene.overrideMaterial

    try {
      for (const entries of renderablesByWriter.values()) {
        for (const entry of entries) {
          const restore = this.restorePool.pop() ?? createRestoreRecord()
          attached.push(attachObject(this.depthScene, entry.object, restore))
        }
      }

      this.depthScene.updateMatrixWorld(true)
      this.depthScene.overrideMaterial = this.depthMaterial
      renderScene.call(graphics, this.depthScene, frame.camera)
    } finally {
      this.depthScene.overrideMaterial = previousOverrideMaterial
      // 以被还原对象自身为根刷新子树矩阵，避免对整个源场景做全量更新
      const restoredRoots = new Set<THREE.Object3D>()
      for (let i = attached.length - 1; i >= 0; i -= 1) {
        const restore = attached[i]
        restoreAttachedObject(restore)
        restoredRoots.add(restore.object)
        restore.object = null!
        restore.parent = null
        this.restorePool.push(restore)
      }
      for (const restoredRoot of restoredRoots) {
        restoredRoot.updateMatrixWorld(true)
      }
    }
  }

  /** Releases writers, diagnostics, and internal depth resources. */
  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    this.writers.clear()
    this.diagnosticsByView.clear()
    this.depthScene.clear()
    this.depthMaterial.dispose()
  }
}

function createRestoreRecord(): AttachedObjectRestore {
  return {
    object: null!,
    parent: null,
    index: -1,
    matrix: new THREE.Matrix4(),
    matrixWorld: new THREE.Matrix4(),
    position: new THREE.Vector3(),
    quaternion: new THREE.Quaternion(),
    scale: new THREE.Vector3(),
    matrixAutoUpdate: true,
  }
}

function attachObject(
  targetScene: THREE.Scene,
  object: THREE.Object3D,
  restore: AttachedObjectRestore
): AttachedObjectRestore {
  restore.object = object
  restore.parent = object.parent
  restore.index = object.parent?.children.indexOf(object) ?? -1
  object.updateMatrixWorld(true)
  restore.matrix.copy(object.matrix)
  restore.matrixWorld.copy(object.matrixWorld)
  restore.position.copy(object.position)
  restore.quaternion.copy(object.quaternion)
  restore.scale.copy(object.scale)
  restore.matrixAutoUpdate = object.matrixAutoUpdate
  targetScene.add(object)
  object.matrixAutoUpdate = false
  object.matrix.copy(restore.matrixWorld)
  object.matrixWorld.copy(restore.matrixWorld)
  return restore
}

function restoreAttachedObject(restore: AttachedObjectRestore): void {
  restore.object.removeFromParent()
  if (restore.parent) {
    restore.parent.add(restore.object)
    const restoredIndex = restore.parent.children.indexOf(restore.object)
    if (restore.index >= 0 && restoredIndex >= 0 && restoredIndex !== restore.index) {
      restore.parent.children.splice(restoredIndex, 1)
      restore.parent.children.splice(
        Math.min(restore.index, restore.parent.children.length),
        0,
        restore.object
      )
    }
  }
  restore.object.matrixAutoUpdate = restore.matrixAutoUpdate
  restore.object.position.copy(restore.position)
  restore.object.quaternion.copy(restore.quaternion)
  restore.object.scale.copy(restore.scale)
  restore.object.matrix.copy(restore.matrix)
  restore.object.matrixWorld.copy(restore.matrixWorld)
}

function collectWriterRenderables(
  writer: SharedOcclusionDepthWriter,
  frame: ViewFrameContext,
  context: PhaseRenderContext
): SharedOcclusionDepthRenderable[] {
  try {
    return Array.from(writer.collect(frame, context) ?? []).filter((entry) => entry.object.visible)
  } catch {
    return []
  }
}
